import { useEffect, useMemo, useState } from "react";

import date from "date-and-time";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";

import { StarIcon } from "@heroicons/react/24/solid";
import { CameraIcon, TruckIcon, UserIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, RowModel, SortingState, Table } from "@tanstack/react-table";

import Avatar from "../../user/Avatar";
import Modal from "../../../layouts/Modal";
import useLeads from "../../../data/useLeads";
import classNames from "../../../utils/classNames";
import useMessages from "../../../data/useMessages";
import BulkCompleteLeads from "./BulkCompleteLeads";
import PriceFormat from "../../../utils/priceFormat";
import openInNewTab from "../../../utils/openInNewTab";
import formatDateTime from "../../../utils/formatDateTime";
import { useSignalR } from "../../../contexts/SignalRContext";
import useLeadsDocuments from "../../../data/useLeadsDocuments";
import useNotification from "../../notifications/useNotifications";
import LeadDocumentCell from "./TableSubComponents/LeadDocumentsCell";
import LeadUnreadMessage from "./TableSubComponents/LeadUnreadMessage";
import { ILead, LeadQualified, LeadStatus } from "../../../models/Lead";
import { BookDropOffModal } from "./TableSubComponents/BookDropOffModal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import LeadsTableCallCount from "./TableSubComponents/LeadsTableCallCount";
import LeadsTableSubComponent from "./TableSubComponents/LeadsTableSubComponent";
import { CollectionReminder, QualifyLead } from "../../../services/TemplateService";
import LeadStatusCell from "../LeadStatusCell";
import PagedTable from "../../shared/Tables/PagedTable";
import TableCheckboxCol from "../../shared/Tables/IndeterminateCheckbox";
import TableVehicleDetails from "../../vehicles/VehiclesTable/TableVehicleDetails";
import TableVehicleContact from "../../vehicles/VehiclesTable/TableVehicleContact";
import Tooltip from "../../shared/Tooltip";


const colHelper = createColumnHelper<ILead>();

const SelectCell = ({ row }: { row: any }) => {
  const isLocal = process.env.NODE_ENV === "development";
  const { activeLeads } = useSignalR();
  const lead = row.original;

  return (
    <div className="flex items-center p-4" onClick={(e) => e.stopPropagation()}>
      {/* Turned this off on local because it was flickering */}
      {/* Render the avatar if the lead is open */}
      {!isLocal && activeLeads && activeLeads[lead.id] && (
        <div className="ml-2 flex">
          {activeLeads[lead.id].map((viewer, key) => (
            <Avatar
              key={key}
              userId={viewer}
              classes="w-8 h-8 rounded-full [&:not(:first-child)]:-ml-2"
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface IUnreadMessage {
  leadId: string;
  unreadMessageCount: number;
}

export default function LeadsTable({ additionalBody, showUnread, unreadMessages }: { additionalBody?: Record<string, any>; showUnread?: boolean; unreadMessages?: IUnreadMessage[]; }) {
  const { pathname } = useLocation();
  const { bulkUpdate, bulkComplete } = useLeads(true, "LeadsTable");
  const { sendText } = useMessages();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("0");
  const [selectedText, setSelectedText] = useState("qualify");
  const [leads, setLeads] = useState<ILead[]>([]);
  const [table, setTable] = useState<Table<ILead>>();

  const { leadsDocuments } = useLeadsDocuments(leads.map((l) => l.id), "LeadsTable");
  const [documents, setDocuments] = useState<any[]>([]);

  useEffect(() => {
    if (leadsDocuments.isLoading) return;
    if (!leadsDocuments.data) return;

    setDocuments(leadsDocuments.data.data);
  }, [leadsDocuments.isLoading, leadsDocuments.data]);


  const handleRowUpdate = () => {
    if (!table) return;

    var leadIds = new Array();

    table.getSelectedRowModel().flatRows.forEach((lead) => {
      leadIds.push(lead.original.id);
    });

    const body = {
      status: parseInt(selectedStatus as keyof LeadStatus),
      leads: leadIds,
    };

    bulkUpdate.mutate(body, {
      onSuccess: () => {
        table.resetRowSelection();
        saveHandlers.onSuccess();
        setUpdateOpen(false);
      },
      onError: saveHandlers.onError,
    });
  };

  const handleBulkText = () => {
    if (!table) return;

    let selectedLeads = new Array();

    table.getSelectedRowModel().flatRows.forEach((lead) => {
      selectedLeads.push(lead.original);
    });

    let templateId = "";

    if (selectedText === "qualify") {
      templateId = QualifyLead.Message;
    } else if (selectedText === "collection") {
      templateId = CollectionReminder.Message;
    }

    selectedLeads.forEach((lead) => {
      const body = {
        leadId: lead.id,
        templateId: templateId,
        contactId: lead.contact.id,
        recipient: lead.contact.telephone1,
        substitutions: {
          "{{name}}": lead.contact?.fullname!,
          "{{make}}": lead.vehicle?.combinedMake!,
          "{{model}}": lead.vehicle?.combinedModel!,
          "{{telephone}}": lead.contact.telephone1!,
          "{{price}}": lead.price.toString()!,
        },
      };

      sendText.mutate(body, {
        onSuccess: () => {
          table.resetRowSelection();
          setTextOpen(false);
          addNotification({
            variant: "success",
            primaryText: "Texts sent successfully",
          });
        },
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Error sending texts",
          });
        },
      });
    });
  };

  const cols = useMemo(() => [
    TableCheckboxCol(true, true),
    colHelper.accessor("vehicle.vrm", {
      header: "Details",
      id: "details",
      sortingFn: "auto",
      cell: (info) => <TableVehicleDetails vehicle={info.row.original.vehicle} />,
    }),
    colHelper.accessor("contactId", {
      header: "Customer",
      id: "details",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col gap-y-2">
        <TableVehicleContact contact={info.row.original.contact} lead={info.row.original} />
        <LeadDocumentCell loading={leadsDocuments.isLoading} documents={documents.filter(d => d.leadId === info.row.original.id)} />
      </div>
    }),
    colHelper.accessor("vehicle.postcode", {
      header: "Address",
      enableSorting: false,
      cell: (info) => (
        <div>
          {!info.row.original.vehicle!.isDropOff
            ? (
              <div>
                <p>{info.getValue()}</p>
                <p>{info.row.original.vehicle?.line1 ? info.row.original.vehicle?.line1 : <span className="text-red-600 font-semibold">No Address Line 1 Found</span>}</p>
              </div>
            )
            : info.row.original.vehicle!.isDropOff && (
              <span className="px-2 py-1 rounded-full font-semibold bg-blue-100 text-blue-500 whitespace-nowrap">
                Drop Off
              </span>
            )}
        </div>
      ),
    }),
    colHelper.accessor("status", {
      header: "Status",
      enableSorting: false,
      cell: (info) => <LeadStatusCell status={info.getValue()} />,
    }),
    colHelper.accessor("callCount", {
      header: "Call List",
      enableSorting: false,
      cell: (info) => <LeadsTableCallCount lead={info.row.original} />,
    }),
    colHelper.accessor("vehicle.scheduledCollectionOn", {
      header: "Collection",
      sortingFn: "auto",
      cell: (info) =>
        info.row.original.vehicle!.isDropOff ? (
          <div onClick={(e) => e.stopPropagation()}>
            <BookDropOffModal currentLead={info.row.original} />
          </div>
        ) : (
          <span>
            {info.getValue() !== null
              ? date.format(new Date(info.getValue()!), "DD/MM/YYYY")
              : "Not Set"}
          </span>
        ),
    }),
    colHelper.accessor("vehicle.enginesWorldwideEngineOffer", {
      header: "Export Value",
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        //Sort by value of both engine export and front end
        return (
          parseFloat(rowA.original.vehicle?.enginesWorldwideEngineOffer ?? "0") +
          parseFloat(rowA.original.vehicle?.enginesWorldwideVfeOffer ?? "0") -
          parseFloat(rowB.original.vehicle?.enginesWorldwideEngineOffer ?? "0") -
          parseFloat(rowB.original.vehicle?.enginesWorldwideVfeOffer ?? "0")
        );
      },
      cell: (info) => (
        <span>
          {PriceFormat(
            parseFloat(
              info.row.original.vehicle?.enginesWorldwideEngineOffer ?? "0"
            ) +
            parseFloat(
              info.row.original.vehicle?.enginesWorldwideVfeOffer ?? "0"
            )
          )}
        </span>
      ),
    }),
    colHelper.accessor("id", {
      header: "Unread Messages",
      sortingFn: "auto",
      cell: (info) => <LeadUnreadMessage lead={info.row.original} />,
    }),
    colHelper.accessor("created", {
      header: "Created",
      sortingFn: "auto",
      cell: (info) => (formatDateTime(DateTime.fromISO(info.getValue()), DateTime.DATETIME_SHORT)),
    }),
  ], [documents, leadsDocuments.isLoading]);

  const handleRowColor = (lead: ILead) => {
    let classNames = "";

    if (!lead.vehicle?.found) classNames = "bg-gray-200 ";
    else if (lead.vehicle.isDropOff === true) classNames = "bg-orange-100 ";
    else if (
      lead.qualified === LeadQualified.Qualified &&
      lead.status === LeadStatus["Awaiting Contact"]
    )
      classNames = "bg-green-200 ";
    else if (
      lead.qualified === LeadQualified.Stop &&
      lead.status === LeadStatus["Awaiting Contact"]
    )
      classNames = "bg-red-200 ";
    else if (
      lead.qualified === LeadQualified["No Match"] &&
      lead.status === LeadStatus["Awaiting Contact"]
    )
      classNames = "bg-yellow-200 ";

    // if (lead.vehicle?.vehiclePremium) {
    //   classNames += "border-4 border-amber-500 "
    // }

    return classNames;
  };

  const leadBasePath = (pathname.startsWith("/bookings") || pathname.startsWith("/quotes")) ? "bookings" : "leads";

  return (
    <>
      <Modal width="max-w-2xl" open={updateOpen} setOpen={setUpdateOpen}>
        <h1 className="text-xl">Bulk Update</h1>
        <div className="mt-4">
          <p>Change all selected leads to which status?</p>
          <div>
            <select
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="mt-2 border border-gray-400 rounded-md text-sm"
              name="leadStatus"
            >
              {Object.values(LeadStatus)
                .filter((ct) => isNaN(Number(ct)))
                .map((status, i) => (
                  <option key={status} value={i}>
                    {status}
                  </option>
                ))}
            </select>
          </div>
          <button
            className="mt-5 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={handleRowUpdate}
          >
            Update
          </button>
        </div>
      </Modal>
      <Modal width="max-w-2xl" open={textOpen} setOpen={setTextOpen}>
        <h1 className="text-xl">Bulk Text</h1>
        <div className="mt-4">
          <p>Send which text to all of these leads?</p>
          <div>
            <select
              onChange={(e) => setSelectedText(e.target.value)}
              className="mt-2 border border-gray-400 rounded-md text-sm"
              name="textType"
            >
              <option key={0} value={"qualify"}>
                Qualifying Text
              </option>
              <option key={1} value={"collection"}>
                Collection Reminder
              </option>
            </select>
          </div>
          <button
            className="mt-5 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
            onClick={handleBulkText}
          >
            Send
          </button>
        </div>
      </Modal>
      <BulkCompleteLeads
        table={table}
        open={completeOpen}
        setOpen={setCompleteOpen}
      />

      <PagedTable
        columns={cols}
        url="/lead/partialwithfilters"
        queryLabel="Search Name, VRM or Postcode"
        expandedRow={(lead: ILead) => <LeadsTableSubComponent lead={lead} />}
        additionalBody={{
          ...additionalBody
        }}
        onRowClick={(x: ILead) => openInNewTab(`${leadBasePath}/${x.id}`)}
        rowColor={handleRowColor}
        setRowData={setLeads}
        actionButtons={[
          {
            label: "Bulk Update",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setUpdateOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
          {
            label: "Bulk Text",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setTextOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
          {
            label: "Bulk Actions",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setCompleteOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
        ]}
      />
    </>
  );
}
